/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import type { HeaderLevel } from '@coursera-web/cds-labs';

import {
  Button,
  Grid,
  Hidden,
  InlineNotification,
  PageGridContainer,
  Typography2,
  breakpoints,
} from '@coursera/cds-core';
import { useTracker } from '@coursera/event-pulse/react';

import { SkillSetCard, SkillSetCardPlaceholder } from 'bundles/page-config-common/components/enterprise/SkillSetCard';
import { MAX_NUMBER_OF_SKILLSETS } from 'bundles/page-config-common/constants/enterpriseConstants';
import { useHeaderLevel } from 'bundles/page-config-common/contexts/HeaderLevelContext';
import type { TargetSkillProficiencySummary } from 'bundles/page-config-common/types/enterprise';

import _t from 'i18n!nls/program-home';

type Props = {
  programSlug: string;
  skillSetLength?: number;
  skillsets: TargetSkillProficiencySummary[];
  hasError: boolean;
  skillsetsLoading: boolean;
};

const styles = {
  wrapper: css`
    gap: var(--cds-spacing-100);
    ${breakpoints.down('xs')} {
      gap: var(--cds-spacing-150);
    }
  `,
};

type HeaderProps = {
  currentLevel: HeaderLevel;
  onViewAllClick?: () => void;
  viewSkillSetsURL: string;
};

const SkillSetHeader = ({ currentLevel, onViewAllClick, viewSkillSetsURL }: HeaderProps) => (
  <Grid item container alignItems="center" justifyContent="space-between">
    <Grid item>
      <Typography2 variant="subtitleLarge" component={currentLevel}>
        {_t('Explore skills by SkillSets')}
      </Typography2>
    </Grid>
    <Grid item component={Hidden} xsDown>
      <Button
        variant="ghost"
        size="small"
        edgeAlign="end"
        onClick={onViewAllClick}
        component="a"
        href={viewSkillSetsURL}
      >
        {_t('View all SkillSets')}
      </Button>
    </Grid>
  </Grid>
);

export const SkillSetCardsModule = ({ programSlug, skillSetLength, skillsets, skillsetsLoading, hasError }: Props) => {
  const { currentLevel } = useHeaderLevel();
  const track = useTracker();

  const viewSkillSetsURL = `/programs/${programSlug}/skillsets`;
  const onViewAllClick = () => {
    track('click_button', {
      button: {
        name: 'view_all_skillsets',
        linkType: 'internal',
        linkURL: viewSkillSetsURL,
        linkName: _t('View all SkillSets'),
      },
    });
  };

  if (!skillsets.length) {
    return null;
  }

  if (skillsetsLoading) {
    const emptyCards = [
      ...Array(
        skillSetLength && skillSetLength <= MAX_NUMBER_OF_SKILLSETS ? skillSetLength : MAX_NUMBER_OF_SKILLSETS
      ).keys(),
    ];
    return (
      <Grid item xs={12} className="SkillSetCards" aria-hidden>
        <SkillSetHeader currentLevel={currentLevel} viewSkillSetsURL={viewSkillSetsURL} />
        <Grid container spacing={{ xs: 8, sm: 24 }}>
          {emptyCards.map((placeholder, index) => (
            <Grid component={Hidden} item md={4} sm={6} xs={12} smDown={index >= 2} key={placeholder}>
              <SkillSetCardPlaceholder />
            </Grid>
          ))}
        </Grid>
      </Grid>
    );
  }

  if (hasError) {
    return (
      <Grid item xs={12} className="SkillSetCards">
        <InlineNotification severity="error" role="alert">
          {_t('Sorry! Something went wrong. Please refresh the page.')}
        </InlineNotification>
      </Grid>
    );
  }

  return (
    <PageGridContainer>
      <Grid item xs={12} container css={styles.wrapper} className="SkillSetCards">
        <SkillSetHeader
          currentLevel={currentLevel}
          onViewAllClick={onViewAllClick}
          viewSkillSetsURL={viewSkillSetsURL}
        />
        <Grid item container spacing={{ xs: 8, sm: 24 }}>
          {skillsets.slice(0, MAX_NUMBER_OF_SKILLSETS).map((skillset, index) => (
            <Grid component={Hidden} item md={4} sm={6} xs={12} smDown={index >= 2} key={skillset.id}>
              <SkillSetCard skillSummary={skillset} programSlug={programSlug} index={index} />
            </Grid>
          ))}
        </Grid>
        <Grid item component={Hidden} smUp xs={12}>
          <Button variant="ghost" size="small" fullWidth component="a" href={viewSkillSetsURL} onClick={onViewAllClick}>
            {_t('View all SkillSets')}
          </Button>
        </Grid>
      </Grid>
    </PageGridContainer>
  );
};

export default SkillSetCardsModule;
